import { useState } from "react";
import { usePack, useToastNotification } from ".";
import { useNotificaitons } from "./useNotifications";
import { PackFileUploader, Uploader } from 'lib/utils/uploadHelpers';
import { useRunSync } from "./useRunSync";
import { packsClient } from "lib/clients";
import { embeddableContentTypes } from "lib/utils";
import { encrypt } from "lib/e2ee";

export const useUploadFiles = (packId: string) => {
  const { data: pack } = usePack(packId);
  const { errorToast } = useToastNotification();
  const { storageLimitNotification, assetLimitNotification} = useNotificaitons()
  const [ percent, setPercent ] = useState<number>(0);
  const [ statusMsg, setStatusMsg ] = useState<string>(null);
  const { sync } = useRunSync(packId);

  const reset = () => {
    setPercent(0);
    setStatusMsg(null);
  }

  const uploadFiles = async (files: any, position?: any) => {
    reset();

    if (files.length === 0) return;

    const fs = [...files];
    const packUploader = new PackFileUploader(packId);
    
    const uploader = new Uploader(packUploader, pack.is_encrypted, fs, (percent: number) => {
      const msg = percent === 100 ? `Creating asset${files.length > 1 ? 's' : ''}` : `Uploading ${files.length} file${files.length > 1 ? 's' : ''}`;

      setPercent(percent);
      setStatusMsg(msg);
    }, pack.key);

    const response = await uploader.upload();
    let order = position?.order;

    //TODO: create assets here  
    for (const file of response.files) {
      const asset = {
        type: 'file',
        upload_token: file.token,
        replace_id: position?.replace_id,
        prompt_id: position?.prompt_id,
        row_id: position?.row_id,
        group_id: position?.group_id
      } as any;

      const data = {
        name: file.data.name, 
        embedded: embeddableContentTypes.includes(file.data.type), 
        filename: file.data.name, 
        content_type: file.data.type
      };

      if (pack.is_encrypted) {
        asset.enc_data = await encrypt(data, pack.key);
      }
      else {
        asset.data = data;
      }

      if (order !== null && order !== undefined) {
        asset.order = order++;
      }

      await packsClient.createAsset(pack.id, asset)
    }

    reset();

    await sync();

    const { successCount, failureCount, tooLargeCount, upgradeRequired, exceededStorage } = response;

    if (exceededStorage) {
      return storageLimitNotification();
    }

    if (upgradeRequired) {
      setTimeout(async () => {
        await uploadFiles(fs, position);
      }, 10000);

      return assetLimitNotification();
    }

    const total = successCount + failureCount + tooLargeCount;

    if (failureCount) {
      const totalFailures = failureCount + tooLargeCount;

      if (totalFailures === total) {
        return errorToast({title: `Failed to upload file${total > 1 ? 's' : ''}. Please check your connection and try again later.`});
      }

      return errorToast({title: `Failed to upload ${totalFailures} out of ${total} files. Please check your connection and try again later.`});
    }

    if (tooLargeCount) {
      if (tooLargeCount === total) {
        return errorToast({title: `Failed to upload file${total > 1 ? 's' : ''}. The max file size allowed is 1GB.`});
      }

      return errorToast({title: `Failed to upload ${tooLargeCount} out of ${total} files. The max file size allowed is 1GB.`});
    }
  }

  return {
    uploadFiles,
    percent,
    statusMsg,
    reset
  }
}
